import { useCallback, useEffect, useRef, useState } from 'react';

// usePersistentState — como useState, pero el valor sobrevive recargas y se
// sincroniza entre pestañas. Persiste en localStorage bajo el prefijo
// `beartrack.helpdesk.` (la key que recibe es relativa a ese prefijo).
//
// Uso:
//   const [pinned, setPinned] = usePersistentState('inbox.pinned', []);
//   const [draft, setDraft]   = usePersistentState(`draft.${conversationId}`, '');
//
// Garantías:
//   - Lectura lazy en el initializer de useState: el primer render ya trae el
//     valor guardado, sin parpadeo de default→persistido.
//   - JSON corrupto en localStorage → cae a initialValue (no rompe el render).
//   - localStorage no disponible (Safari privado, cookies bloqueadas) → funciona
//     como useState normal, en memoria.
//   - Se escribe solo cuando el valor CAMBIA: montar no ensucia localStorage con
//     los defaults.

const STORAGE_PREFIX = 'beartrack.helpdesk.';

// null = no hay storage utilizable; undefined = todavía sin comprobar.
let storageCache;

/**
 * Safari en modo privado expone window.localStorage pero lanza QuotaExceededError
 * al escribir: no basta con comprobar que exista, hay que probar un write real.
 * El resultado se cachea a nivel de módulo (la capacidad no cambia en runtime).
 * @returns {Storage|null}
 */
function getStorage() {
  if (storageCache !== undefined) return storageCache;
  storageCache = null;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return storageCache;
    const probeKey = `${STORAGE_PREFIX}__probe__`;
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    storageCache = window.localStorage;
  } catch {
    storageCache = null;
  }
  return storageCache;
}

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    // Ciclos o valores no serializables: se mantiene en memoria, no se persiste.
    return undefined;
  }
}

function resolveInitial(initialValue) {
  return typeof initialValue === 'function' ? initialValue() : initialValue;
}

function readStored(storageKey, initialValue) {
  const storage = getStorage();
  if (!storage) return resolveInitial(initialValue);
  try {
    const raw = storage.getItem(storageKey);
    if (raw === null) return resolveInitial(initialValue);
    return JSON.parse(raw);
  } catch {
    return resolveInitial(initialValue);
  }
}

/**
 * @param {string} key - Sufijo de la clave; se guarda como `beartrack.helpdesk.${key}`
 * @param {*} initialValue - Valor por defecto (o función lazy, como en useState)
 * @returns {[*, function]} - [valor, setValor] con la misma API que useState
 */
export function usePersistentState(key, initialValue) {
  const storageKey = `${STORAGE_PREFIX}${key}`;

  const [state, setState] = useState(() => readStored(storageKey, initialValue));

  // initialValue puede ser un literal nuevo en cada render; se lee por ref para
  // no meterlo en dependencias de efectos.
  const initialRef = useRef(initialValue);
  initialRef.current = initialValue;

  // Último JSON conocido de esta clave (escrito por nosotros o recibido de otra
  // pestaña). Evita el ping-pong: sin esto, el eco del evento 'storage' vuelve a
  // escribir el mismo valor y las pestañas se re-notifican en bucle.
  const lastRawRef = useRef(undefined);
  if (lastRawRef.current === undefined) lastRawRef.current = safeStringify(state);

  // Si cambia la key (p. ej. un borrador por conversación) hay que releer YA,
  // en render: hacerlo en un effect mostraría un frame con el valor de la key
  // anterior.
  const prevKeyRef = useRef(storageKey);
  if (prevKeyRef.current !== storageKey) {
    prevKeyRef.current = storageKey;
    const next = readStored(storageKey, initialRef.current);
    lastRawRef.current = safeStringify(next);
    setState(next);
  }

  useEffect(() => {
    const raw = safeStringify(state);
    if (raw === undefined) return;
    if (raw === lastRawRef.current) return;
    lastRawRef.current = raw;
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.setItem(storageKey, raw);
    } catch {
      // Cuota llena / storage revocado a mitad de sesión: se sigue en memoria.
    }
  }, [storageKey, state]);

  useEffect(() => {
    if (typeof window === 'undefined' || !getStorage()) return undefined;

    const onStorage = (event) => {
      if (event.key !== storageKey) return;
      if (event.storageArea && event.storageArea !== window.localStorage) return;

      // Clave borrada (o localStorage.clear()) en otra pestaña → volver al
      // default sin re-escribirla: resucitarla desharía el borrado.
      if (event.newValue === null) {
        const fallback = resolveInitial(initialRef.current);
        lastRawRef.current = safeStringify(fallback);
        setState(fallback);
        return;
      }

      if (event.newValue === lastRawRef.current) return;
      try {
        const parsed = JSON.parse(event.newValue);
        lastRawRef.current = event.newValue;
        setState(parsed);
      } catch {
        // Otra pestaña escribió algo no-JSON: se ignora y se conserva el estado.
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  const setPersistentState = useCallback((next) => {
    setState((prev) => (typeof next === 'function' ? next(prev) : next));
  }, []);

  return [state, setPersistentState];
}

export default usePersistentState;
